import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Clock3, Play, Square } from "lucide-react";

const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds)) {
    return "—";
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remaining = seconds % 60;
  return hours > 0
    ? `${hours}h ${String(minutes).padStart(2, "0")}m ${String(remaining).padStart(2, "0")}s`
    : `${minutes}m ${String(remaining).padStart(2, "0")}s`;
};

const formatTimestamp = (value) => {
  if (!value) {
    return "—";
  }
  return new Date(value).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

export function SessionSummary({ session, testId = "session-summary" }) {
  if (!session) {
    return null;
  }
  return (
    <div className="mt-3 border-t border-slate-200 pt-3 text-sm" data-testid={testId}>
      <p className="font-semibold text-slate-900">Consultation {session.status}</p>
      <dl className="mt-2 grid gap-1 text-xs text-slate-600 sm:grid-cols-2">
        <div>
          <dt className="text-slate-500">Session ID</dt>
          <dd className="font-medium text-slate-800">{session.display_id}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Actual start</dt>
          <dd className="font-medium text-slate-800">{formatTimestamp(session.actual_start_utc)}</dd>
        </div>
        {session.actual_end_utc && (
          <div>
            <dt className="text-slate-500">Actual end</dt>
            <dd className="font-medium text-slate-800">{formatTimestamp(session.actual_end_utc)}</dd>
          </div>
        )}
        {session.status === "ENDED" && (
          <div>
            <dt className="text-slate-500">Actual duration</dt>
            <dd className="font-medium text-slate-800">
              {formatDuration(session.actual_duration_seconds)}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

export default function ExpertSessionControls({ appointment, state, onChange }) {
  const [confirmation, setConfirmation] = useState("");
  const [working, setWorking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const session = state?.session;

  useEffect(() => {
    if (session?.status !== "ACTIVE" || !session.actual_start_utc) {
      setElapsed(0);
      return undefined;
    }
    const updateElapsed = () => {
      const start = new Date(session.actual_start_utc).getTime();
      setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    };
    updateElapsed();
    const interval = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(interval);
  }, [session?.actual_start_utc, session?.status]);

  const act = async (action) => {
    setWorking(true);
    try {
      await api.post(`/expert-sessions/expert/appointments/${appointment.appointment_id}/${action}`);
      toast.success(action === "start" ? "Consultation started" : "Consultation ended");
      setConfirmation("");
      onChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update consultation session");
    } finally {
      setWorking(false);
    }
  };

  if (appointment.status === "CANCELLED") {
    return null;
  }

  return (
    <div className="mt-3 border-t border-slate-200 pt-3" data-testid={`session-controls-${appointment.appointment_id}`}>
      {session?.status === "ACTIVE" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 bg-emerald-50 p-3">
            <div>
              <p className="font-semibold text-emerald-900" data-testid={`session-active-${appointment.appointment_id}`}>
                Consultation Active
              </p>
              <p className="mt-1 text-xs text-emerald-800">
                Display timer uses the saved server start time.
              </p>
            </div>
            <p className="font-mono text-lg font-bold text-emerald-900" data-testid={`session-timer-${appointment.appointment_id}`}>
              {formatDuration(elapsed)}
            </p>
          </div>
          <Button
            className="w-full"
            data-testid={`session-end-${appointment.appointment_id}`}
            onClick={() => setConfirmation("end")}
            type="button"
            variant="outline"
          >
            <Square className="mr-2 h-4 w-4" />
            End Consultation
          </Button>
        </div>
      )}
      {session?.status === "ENDED" && (
        <SessionSummary
          session={session}
          testId={`session-ended-${appointment.appointment_id}`}
        />
      )}
      {!session && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Clock3 className="h-4 w-4" />
            <span data-testid={`session-readiness-${appointment.appointment_id}`}>
              {state?.ready_to_start ? "Ready to Start" : "Scheduled"}
            </span>
          </div>
          {state?.ready_to_start && (
            <Button
              className="w-full bg-emerald-700 hover:bg-emerald-800"
              data-testid={`session-start-${appointment.appointment_id}`}
              onClick={() => setConfirmation("start")}
              type="button"
            >
              <Play className="mr-2 h-4 w-4" />
              Start Consultation
            </Button>
          )}
        </div>
      )}

      <Dialog open={Boolean(confirmation)} onOpenChange={(open) => !open && setConfirmation("")}>
        <DialogContent data-testid={`session-${confirmation}-confirmation`}>
          <DialogHeader>
            <DialogTitle className="font-display">
              {confirmation === "start" ? "Start consultation now?" : "End this consultation?"}
            </DialogTitle>
            <DialogDescription>
              {confirmation === "start"
                ? "The server will record the consultation start time."
                : "The server will record the end time and final actual duration."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid={`session-${confirmation}-dismiss`}
              disabled={working}
              onClick={() => setConfirmation("")}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              data-testid={`session-${confirmation}-confirm`}
              disabled={working}
              onClick={() => act(confirmation)}
              type="button"
            >
              {working ? "Saving…" : confirmation === "start" ? "Start consultation" : "End consultation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { formatDuration, formatTimestamp };